import React, { useState } from 'react';
import { Loader2, Package, Calendar, RotateCcw, Star, Trophy, FileText, Gift, ListTodo, MessageSquare, ChevronDown, ExternalLink, Mail, MailOpen } from 'lucide-react';
import { fmtDate } from '@/lib/utils';

const ICONS = {
  order: Package, booking: Calendar, rma: RotateCcw, review: Star,
  session: Trophy, disclaimer: FileText, giftcard: Gift, task: ListTodo,
  feedback: MessageSquare, email: Mail,
};

const STATUS_COLOR = (s) =>
  (s === 'completed' || s === 'paid' || s === 'confirmed' || s === 'approved' || s === 'active')
    ? 'text-green-400'
    : (s === 'cancelled' || s === 'rejected' || s === 'failed')
      ? 'text-red-400'
      : 'text-white/50';

export default function CustomerTimeline({ timeline = [], stats = {}, loading, onNavigate }) {
  const [openId, setOpenId] = useState(null);

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  // Each row expands on click to reveal the full detail for that activity, and
  // — where a deep-link exists — an action button that jumps to the matching
  // admin section (closes the customer modal). Orders and bookings live in
  // their own tabs, so the timeline covers everything else.
  return (
    <div className="space-y-3">
      <p className="text-white/30 text-xs">Full activity history. Click any item to expand its detail and act on it.</p>
      {timeline.length === 0 ? (
        <p className="text-white/30 text-xs py-8 text-center border border-white/5 bg-[#0d0d0f]">
          No activity recorded for this customer yet.
        </p>
      ) : (
        <div className="space-y-2">
          {timeline.map((ev, i) => {
            // Inbound replies are read differently from messages we sent: they
            // get an accent edge, their own icon and an unread flag.
            const isInbound = ev.type === 'email' && ev.direction === 'inbound';
            const Icon = isInbound ? MailOpen : (ICONS[ev.type] || ListTodo);
            const isOpen = openId === `${ev.type}-${i}`;
            return (
              <div
                key={`${ev.type}-${i}`}
                className={`border bg-[#0d0d0f] ${
                  isInbound
                    ? 'border-l-2 border-l-primary border-y-white/5 border-r-white/5'
                    : 'border-white/5'
                } ${ev.unread ? 'bg-primary/[0.04]' : ''} ${ev.muted ? 'opacity-60' : ''}`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : `${ev.type}-${i}`)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#111115] transition-colors"
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${isInbound ? 'text-primary' : 'text-white/40'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">
                      {ev.title}
                      {ev.unread && (
                        <span className="ml-2 align-middle bg-primary text-white text-[8px] font-bold tracking-widest px-1.5 py-0.5">
                          UNREAD
                        </span>
                      )}
                    </p>
                    <p className="text-white/40 text-xs truncate">{ev.subtitle}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {ev.amount != null && <p className="text-white/70 text-xs font-bold">£{Number(ev.amount).toFixed(2)}</p>}
                    {ev.status && <p className={`text-[10px] font-bold tracking-wider ${STATUS_COLOR(ev.status)}`}>{ev.status.toUpperCase()}</p>}
                    {ev.date && <p className="text-white/30 text-[10px] mt-0.5">{fmtDate(ev.date)}</p>}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-white/30 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5">
                    {ev.detail && (
                      <p className="text-white/60 text-xs leading-relaxed mt-3">{ev.detail}</p>
                    )}
                    {ev.link && onNavigate && (
                      <button
                        onClick={() => onNavigate(ev.link)}
                        className="mt-3 inline-flex items-center gap-1.5 text-primary hover:text-primary/80 text-[11px] font-bold tracking-wider transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> {ev.link_label || 'OPEN'}
                      </button>
                    )}
                    {!ev.link && ev.type === 'task' && (
                      <p className="mt-3 text-white/30 text-[11px]">Manage this task in the Tasks &amp; Reminders tab above.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}